<div class="entity-config-page container-fluid">
  <blocking-loader-overlay
    :show="loading_calendar"
    :message="page_loading_message"
    :detail="page_loading_detail"
  />
  <div class="entity-config-shell d-flex flex-column gap-0 mx-auto">
    <section class="entity-config-top rounded-4 overflow-hidden shadow-sm mb-1">
      <div class="entity-config-top-hero px-2 px-md-2 pt-2 pb-1">
        <div class="row g-2 align-items-start">
          <div class="col">
            <div class="text-uppercase text-xxs text-white opacity-75 d-flex align-items-center gap-2 mb-2">
              <span>Home</span>
              <span>/</span>
              <span>Calendar</span>
            </div>
            <h1 class="entity-page-title text-white lh-sm mb-1">Calendar</h1>
          </div>
        </div>
      </div>

      <div class="px-2 px-md-2 pb-0">
        <section class="row g-2 mt-1">
          <div class="col-12">
            <div class="entity-detail-panel rounded-4 border-0 p-0 h-100 mt-0 overflow-hidden">
              <div class="entity-vibrant-binding-card rounded-4 border-0 shadow">
                <div class="d-flex flex-column flex-xl-row align-items-xl-center justify-content-between gap-3 mb-3">
                  <div class="case-owner-calendar-range bg-white d-inline-flex align-items-center rounded-4 border overflow-hidden shadow">
                    <button type="button" class="btn border-0 rounded-0 px-3 small secondary-color" @click="previous_range">&larr;</button>
                    <div class="px-3 py-2 small fw-semibold secondary-color">{{ range_label }}</div>
                    <button type="button" class="btn border-0 rounded-0 px-3 small secondary-color" @click="next_range">&rarr;</button>
                    <button type="button" class="btn border-0 border-start rounded-0 px-4 small secondary-color" @click="go_to_today">Today</button>
                  </div>

                  <div class="d-flex align-items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      class="btn btn-sm rounded-pill px-3 shadow-sm"
                      :class="view_mode === 'week' ? 'btn-primary-dark' : 'btn-light border secondary-color'"
                      @click="set_view_mode('week')"
                    >
                      Week
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm rounded-pill px-3 shadow-sm"
                      :class="view_mode === 'day' ? 'btn-primary-dark' : 'btn-light border secondary-color'"
                      @click="set_view_mode('day')"
                    >
                      Day
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm rounded-pill px-3 shadow-sm"
                      :class="view_mode === 'month' ? 'btn-primary-dark' : 'btn-light border secondary-color'"
                      @click="set_view_mode('month')"
                    >
                      Month
                    </button>
                    <button type="button" class="btn btn-primary-dark btn-sm rounded-small px-3" @click="load_calendar_data">Refresh</button>
                  </div>
                </div>

                <div class="case-owner-calendar-legend bg-white rounded-4 border px-3 py-3 mb-3 shadow">
                  <div class="d-flex align-items-center gap-4 flex-wrap">
                    <div v-for="item in legend_items" :key="item.label" class="d-flex align-items-center gap-2 small">
                      <span class="status-dot-md rounded-circle d-inline-block" :class="item.dot_class"></span>
                      <span class="secondary-color">{{ item.label }}</span>
                    </div>
                  </div>
                </div>

                <div v-if="calendar_error" class="alert alert-warning py-2 px-3 mb-3" role="alert">
                  {{ calendar_error }}
                </div>

                <div v-if="view_mode !== 'month'" class="case-owner-calendar-board rounded-4 border overflow-hidden mb-3 shadow">
                  <div class="case-owner-calendar-grid">
                    <div class="case-owner-calendar-time-col">
                      <div class="case-owner-calendar-time-spacer"></div>
                      <div v-for="slot in time_slots" :key="slot" class="case-owner-calendar-time">{{ slot }}</div>
                    </div>

                    <div
                      v-for="day in week_days"
                      :key="day.key"
                      class="case-owner-calendar-day-col"
                    >
                      <div class="case-owner-calendar-day-head" :class="{ 'is-today': day.is_today }">
                        <span>{{ day.label }}</span>
                        <span v-if="day.is_today" class="badge rounded-pill fw-normal case-owner-calendar-today-pill">Today</span>
                      </div>

                      <div class="case-owner-calendar-day-body">
                        <div class="case-owner-calendar-slot-line" v-for="slot in time_slots" :key="`${day.key}-${slot}`"></div>

                        <div
                          v-if="!loading_calendar && !day.events.length"
                          class="small secondary-color px-3 pt-3"
                        >
                          No SLA deadlines
                        </div>

                        <article
                          v-for="event in day.events"
                          :key="event.id"
                          class="case-owner-calendar-event rounded-3 p-2"
                          :class="event.card_class"
                          :style="event.style"
                          role="button"
                          tabindex="0"
                          @click="open_event(event)"
                        >
                          <div class="case-owner-calendar-event-meta">{{ event.start }} <span v-if="event.duration">- {{ event.duration }}</span></div>
                          <div class="case-owner-calendar-event-title">{{ event.title }}</div>
                          <div v-if="event.meta" class="case-owner-calendar-event-sub">{{ event.meta }}</div>
                        </article>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else class="case-owner-calendar-board case-owner-calendar-month-board rounded-4 border overflow-hidden mb-3 shadow">
                  <div class="case-owner-calendar-month-grid">
                    <div v-for="label in ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']" :key="label" class="case-owner-calendar-month-weekday">
                      {{ label }}
                    </div>

                    <div
                      v-for="day in month_days"
                      :key="day.key"
                      class="case-owner-calendar-month-day"
                      :class="{ 'is-outside-month': !day.in_month, 'is-today': day.is_today }"
                    >
                      <div class="case-owner-calendar-month-day-number">{{ day.label }}</div>
                      <article
                        v-for="event in day.events"
                        :key="event.id"
                        class="case-owner-calendar-event case-owner-calendar-month-event rounded-3 p-2"
                        :class="event.card_class"
                        role="button"
                        tabindex="0"
                        @click="open_event(event)"
                      >
                        <div class="case-owner-calendar-event-meta">{{ event.start }}</div>
                        <div class="case-owner-calendar-event-title">{{ event.title }}</div>
                      </article>
                    </div>
                  </div>
                </div>

                <div class="d-flex flex-column flex-xl-row align-items-xl-center justify-content-between gap-3">
                  <div class="case-owner-calendar-summary rounded-4 px-4 py-3">
                    <span class="case-owner-calendar-summary-number">{{ summary.total_events }}</span>
                    <span class="secondary-color">{{ view_mode === 'day' ? 'deadline' : 'deadlines in view' }}</span>
                    <span class="secondary-color">-</span>
                    <span class="fw-semibold text-danger-emphasis">{{ summary.overdue }} overdue</span>
                    <span class="secondary-color">-</span>
                    <span class="fw-semibold text-warning-emphasis">{{ summary.due_today }} due today</span>
                  </div>

                  <div class="text-uppercase text-xxs secondary-color case-owner-calendar-sync">
                    {{ sync_label }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </section>
  </div>
</div>
